import { getServiceUrl } from '@airclou/utils';
import { HTTP_TIMEOUT_MS } from '../config';
import { apiErrorFrom } from './errors';
import { fileService } from '../services/fileService';

const API_BASE_URL = getServiceUrl('dashboard');

const fetchT: typeof fetch = (input, init) =>
  fetch(input, { ...init, signal: init?.signal ?? AbortSignal.timeout(HTTP_TIMEOUT_MS) });

export interface WorkspaceMemberPreview {
  user_id: string;
  role: string;
  name: string;
  email?: string;
  avatar?: string;
}

export interface WorkspaceAppPreview {
  app_id: string;
  app_name: string;
  app_slug: string;
}

export interface Workspace {
  id: string;
  company_id: string;
  tenant_id: string;
  name: string;
  slug: string;
  logo_url: string | null;
  metadata: Record<string, any>;
  created_at: string;
  updated_at: string;
  created_by: string | null;
  company_name?: string;
  user_count?: number;
  app_count?: number;
  members_preview?: WorkspaceMemberPreview[];
  apps_preview?: WorkspaceAppPreview[];
}

export interface WorkspaceUser {
  user_id: string;
  workspace_id: string;
  role: 'admin' | 'member';
  access_type?: 'direct' | 'group' | 'both';
  groups?: string[];
  created_at: string;
  user?: {
    email: string;
    name: string;
  };
}

export interface WorkspaceApp {
  app_id: string;
  workspace_id: string;
  enabled_at: string;
  default_member_role: string | null;
  app?: {
    name: string;
    slug: string;
  };
}

export interface WorkspaceUserAppRole {
  app_id: string;
  app_name: string;
  app_slug: string;
  role: string;
}

export interface WorkspaceUserDetails {
  user_id: string;
  workspace_id: string;
  workspace_name: string;
  workspace_role: 'admin' | 'member';
  joined_at: string;
  user: {
    email: string;
    name: string;
    first_name?: string;
    last_name?: string;
    avatar_url?: string;
    status?: string;
  };
  app_roles: WorkspaceUserAppRole[];
  enabled_apps: Array<{
    app_id: string;
    app_name: string;
    app_slug: string;
  }>;
}

export interface CreateWorkspaceInput {
  name: string;
  slug: string;
  company_id: string;
  metadata?: Record<string, any>;
}

export interface UpdateWorkspaceInput {
  name?: string;
  slug?: string;
  logo_url?: string | null;
}

export interface AddWorkspaceUserInput {
  user_id: string;
  role: 'admin' | 'member';
}

export interface UpdateWorkspaceUserAppRolesInput {
  app_roles: Array<{
    app_id: string;
    role: string;
  }>;
}

// Workspace Group Assignment Types
export interface WorkspaceGroup {
  assignment_id: string;
  group_id: string;
  group_name: string;
  group_description?: string;
  created_at: string;
  created_by: string;
  member_count: number;
}

export interface AvailableGroup {
  id: string;
  name: string;
  description?: string;
  member_count: number;
}

export interface GroupRemovalImpact {
  will_lose_access: number;
  will_retain_access: number;
}

// Error codes for group assignment operations (mirroring backend)
export const WorkspaceGroupAssignmentErrorCodes = {
  GROUP_NOT_FOUND: 'GROUP_NOT_FOUND',
  GROUP_ALREADY_ASSIGNED: 'GROUP_ALREADY_ASSIGNED',
  GROUP_NOT_ASSIGNED: 'GROUP_NOT_ASSIGNED',
  CROSS_COMPANY_ASSIGNMENT: 'CROSS_COMPANY_ASSIGNMENT',
} as const;

// Get auth token from localStorage
function getAuthToken(): string | null {
  return localStorage.getItem('dashboard:authToken');
}

// API functions
export async function listWorkspaces(companyId?: string): Promise<{ workspaces: Workspace[] }> {
  const url = companyId
    ? `${API_BASE_URL}/api/workspaces?company_id=${companyId}`
    : `${API_BASE_URL}/api/workspaces`;

  const response = await fetchT(url, {
    headers: {
      'Authorization': `Bearer ${getAuthToken()}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error('Failed to fetch workspaces');
  }

  const json = await response.json();
  return json.data;
}

export async function getWorkspace(id: string): Promise<{ workspace: Workspace }> {
  const response = await fetchT(`${API_BASE_URL}/api/workspaces/${id}`, {
    headers: {
      'Authorization': `Bearer ${getAuthToken()}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    // Carries the status so the detail page can tell "this workspace is gone"
    // from "the request failed", and the server's message instead of a generic
    // string that said nothing about what went wrong.
    throw await apiErrorFrom(response, 'Failed to fetch workspace');
  }

  const json = await response.json();
  return json.data;
}

export async function createWorkspace(data: CreateWorkspaceInput): Promise<{ workspace: Workspace }> {
  const response = await fetchT(`${API_BASE_URL}/api/workspaces`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${getAuthToken()}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json();
    // The code lets the dialog tell the plan's workspace cap
    // (WORKSPACE_LIMIT_REACHED) from a failure.
    throw Object.assign(new Error(error.error?.message || error.error || 'Failed to create workspace'), {
      code: error.error?.code as string | undefined,
    });
  }

  const json = await response.json();
  return json.data;
}

export async function updateWorkspace(id: string, data: UpdateWorkspaceInput): Promise<{ workspace: Workspace }> {
  const response = await fetchT(`${API_BASE_URL}/api/workspaces/${id}`, {
    method: 'PATCH',
    headers: {
      'Authorization': `Bearer ${getAuthToken()}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error?.message || error.error || 'Failed to update workspace');
  }

  const json = await response.json();
  return json.data;
}

export async function deleteWorkspace(id: string): Promise<void> {
  const response = await fetchT(`${API_BASE_URL}/api/workspaces/${id}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${getAuthToken()}`,
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error?.message || error.error || 'Failed to delete workspace');
  }
}

export async function listWorkspaceUsers(workspaceId: string): Promise<{ users: WorkspaceUser[] }> {
  const response = await fetchT(`${API_BASE_URL}/api/workspaces/${workspaceId}/users`, {
    headers: {
      'Authorization': `Bearer ${getAuthToken()}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error('Failed to fetch workspace users');
  }

  const json = await response.json();
  return json.data;
}

export async function addWorkspaceUser(workspaceId: string, data: AddWorkspaceUserInput): Promise<void> {
  const response = await fetchT(`${API_BASE_URL}/api/workspaces/${workspaceId}/users`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${getAuthToken()}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error?.message || error.error || 'Failed to add user to workspace');
  }
}

export async function removeWorkspaceUser(workspaceId: string, userId: string): Promise<void> {
  const response = await fetchT(`${API_BASE_URL}/api/workspaces/${workspaceId}/users/${userId}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${getAuthToken()}`,
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error?.message || error.error || 'Failed to remove user from workspace');
  }
}

export async function listWorkspaceApps(workspaceId: string): Promise<{ apps: WorkspaceApp[] }> {
  const response = await fetchT(`${API_BASE_URL}/api/workspaces/${workspaceId}/apps`, {
    headers: {
      'Authorization': `Bearer ${getAuthToken()}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error('Failed to fetch workspace apps');
  }

  const json = await response.json();
  const data = json.data;

  // Transform flat response to nested structure expected by frontend
  return {
    apps: data.apps.map((app: any) => ({
      app_id: app.app_id,
      workspace_id: workspaceId,
      enabled_at: app.enabled_at,
      default_member_role: app.default_member_role,
      app: app.app_name ? {
        name: app.app_name,
        slug: app.app_slug,
      } : undefined,
    })),
  };
}

export async function listAvailableApps(workspaceId: string): Promise<{ apps: Array<{ id: string; name: string; slug: string }> }> {
  const response = await fetchT(`${API_BASE_URL}/api/workspaces/${workspaceId}/apps/available`, {
    headers: {
      'Authorization': `Bearer ${getAuthToken()}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error('Failed to fetch available apps');
  }

  const json = await response.json();
  return { apps: json.data };
}

export async function addWorkspaceApp(workspaceId: string, appId: string, defaultMemberRole?: string | null): Promise<void> {
  const response = await fetchT(`${API_BASE_URL}/api/workspaces/${workspaceId}/apps`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${getAuthToken()}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ app_id: appId, default_member_role: defaultMemberRole }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error?.message || error.error || 'Failed to add app to workspace');
  }
}

export async function removeWorkspaceApp(workspaceId: string, appId: string): Promise<void> {
  const response = await fetchT(`${API_BASE_URL}/api/workspaces/${workspaceId}/apps/${appId}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${getAuthToken()}`,
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error?.message || error.error || 'Failed to remove app from workspace');
  }
}

/**
 * Get detailed information about a user within a workspace, including their app roles
 */
export async function getWorkspaceUserDetails(
  workspaceId: string, 
  userId: string
): Promise<WorkspaceUserDetails> {
  const response = await fetchT(`${API_BASE_URL}/api/workspaces/${workspaceId}/users/${userId}/details`, {
    headers: {
      'Authorization': `Bearer ${getAuthToken()}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw await apiErrorFrom(response, 'Failed to fetch workspace user details');
  }

  const json = await response.json();
  return json.data;
}

/**
 * Update a user's app roles within a workspace
 */
export async function updateWorkspaceUserAppRoles(
  workspaceId: string,
  userId: string,
  data: UpdateWorkspaceUserAppRolesInput
): Promise<{ success: boolean; message: string; changes: { added: number; updated: number; removed: number } }> {
  const response = await fetchT(`${API_BASE_URL}/api/workspaces/${workspaceId}/users/${userId}/app-roles`, {
    method: 'PUT',
    headers: {
      'Authorization': `Bearer ${getAuthToken()}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    throw await apiErrorFrom(response, 'Failed to update user app roles');
  }

  const json = await response.json();
  return json.data;
}

/**
 * Update a user's workspace role (admin or member)
 */
export async function updateWorkspaceUserRole(
  workspaceId: string,
  userId: string,
  role: 'admin' | 'member'
): Promise<{ user: { workspace_id: string; user_id: string; role: string; created_at: string } }> {
  const response = await fetchT(`${API_BASE_URL}/api/workspaces/${workspaceId}/users/${userId}`, {
    method: 'PATCH',
    headers: {
      'Authorization': `Bearer ${getAuthToken()}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ role }),
  });

  if (!response.ok) {
    throw await apiErrorFrom(response, 'Failed to update workspace user role');
  }

  const json = await response.json();
  return json.data;
}

/**
 * Update the default member role for an app in a workspace.
 * When set, workspace members get this role when accessing the app (unless they have an explicit override).
 */
export async function updateWorkspaceAppDefaultRole(
  workspaceId: string,
  appId: string,
  defaultMemberRole: string | null
): Promise<{ app: { id: string; workspace_id: string; app_id: string; default_member_role: string | null; updated_at: string } }> {
  const response = await fetchT(`${API_BASE_URL}/api/workspaces/${workspaceId}/apps/${appId}`, {
    method: 'PATCH',
    headers: {
      'Authorization': `Bearer ${getAuthToken()}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ default_member_role: defaultMemberRole }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error?.message || error.error || 'Failed to update app default role');
  }

  const json = await response.json();
  return json.data;
}

/**
 * Search users in the tenant for adding to workspaces
 * Uses the team members endpoint with search functionality
 */
export interface TenantUser {
  user_id: string;
  email: string;
  name: string;
  first_name?: string;
  last_name?: string;
  avatar_url?: string;
  role: string;
  status: string;
}

export async function searchTenantUsers(
  search: string,
  limit: number = 20,
  excludeUserIds: string[] = []
): Promise<{ users: TenantUser[]; total: number; hasMore: boolean }> {
  const params = new URLSearchParams({
    limit: limit.toString(),
    status: 'active', // Only show active users
  });
  
  if (search) {
    params.append('search', search);
  }

  const response = await fetchT(`${API_BASE_URL}/api/team/members?${params.toString()}`, {
    headers: {
      'Authorization': `Bearer ${getAuthToken()}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error('Failed to search users');
  }

  const json = await response.json();
  const members = json.data || [];
  const meta = json.meta;

  // Filter out excluded users and deduplicate by user_id
  const userMap = new Map<string, TenantUser>();

  for (const member of members) {
    if (!excludeUserIds.includes(member.user_id) && !userMap.has(member.user_id)) {
      userMap.set(member.user_id, {
        user_id: member.user_id,
        email: member.email || '',
        name: member.name || member.email || member.user_id,
        first_name: member.first_name,
        last_name: member.last_name,
        avatar_url: member.avatar_url,
        role: member.role || 'member',
        status: member.status || 'active',
      });
    }
  }

  const users = Array.from(userMap.values());

  return {
    users,
    total: meta?.total || users.length,
    hasMore: meta ? meta.page < meta.totalPages : false,
  };
}

// ============================================================================
// WORKSPACE GROUP ASSIGNMENT
// ============================================================================

/**
 * List all groups assigned to a workspace
 */
export async function listWorkspaceGroups(workspaceId: string): Promise<{ groups: WorkspaceGroup[] }> {
  const response = await fetchT(`${API_BASE_URL}/api/workspaces/${workspaceId}/groups`, {
    headers: {
      'Authorization': `Bearer ${getAuthToken()}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error?.message || error.error || 'Failed to fetch workspace groups');
  }

  const json = await response.json();
  return { groups: json.data?.groups || [] };
}

/**
 * List groups available to be assigned to this workspace (same company, not already assigned)
 */
export async function listAvailableGroups(workspaceId: string): Promise<{ groups: AvailableGroup[] }> {
  const response = await fetchT(`${API_BASE_URL}/api/workspaces/${workspaceId}/groups/available`, {
    headers: {
      'Authorization': `Bearer ${getAuthToken()}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error?.message || error.error || 'Failed to fetch available groups');
  }

  const json = await response.json();
  return { groups: json.data?.groups || [] };
}

/**
 * Assign a group to a workspace
 */
export async function addGroupToWorkspace(
  workspaceId: string,
  groupId: string
): Promise<{ assignment_id: string; group_id: string }> {
  const response = await fetchT(`${API_BASE_URL}/api/workspaces/${workspaceId}/groups`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${getAuthToken()}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ group_id: groupId }),
  });

  if (!response.ok) {
    const error = await response.json();
    // Throw with error code if available
    const err = new Error(error.error?.message || error.error || 'Failed to add group to workspace') as Error & { code?: string };
    err.code = error.error?.code || error.code;
    throw err;
  }

  const json = await response.json();
  return json.data;
}

/**
 * Remove a group from a workspace
 */
export async function removeGroupFromWorkspace(
  workspaceId: string,
  groupId: string
): Promise<void> {
  const response = await fetchT(`${API_BASE_URL}/api/workspaces/${workspaceId}/groups/${groupId}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${getAuthToken()}`,
    },
  });

  if (!response.ok) {
    const error = await response.json();
    const err = new Error(error.error?.message || error.error || 'Failed to remove group from workspace') as Error & { code?: string };
    err.code = error.error?.code || error.code;
    throw err;
  }
}

/**
 * Get the impact preview of removing a group from a workspace
 */
export async function getGroupRemovalImpact(
  workspaceId: string,
  groupId: string
): Promise<GroupRemovalImpact> {
  const response = await fetchT(`${API_BASE_URL}/api/workspaces/${workspaceId}/groups/${groupId}/removal-impact`, {
    headers: {
      'Authorization': `Bearer ${getAuthToken()}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error?.message || error.error || 'Failed to get removal impact');
  }

  const json = await response.json();
  return json.data;
}

// ============================================================================
// WORKSPACE LOGO OPERATIONS
// ============================================================================

export async function uploadWorkspaceLogo(workspaceId: string, file: File): Promise<{ url: string }> {
  const result = await fileService.uploadWorkspaceLogo(file, workspaceId);
  if (!result.success) {
    throw new Error(result.error?.code || 'UPLOAD_FAILED');
  }
  return { url: result.data.url };
}

export async function updateWorkspaceLogo(workspaceId: string, logoUrl: string | null): Promise<{ workspace: Workspace }> {
  return updateWorkspace(workspaceId, { logo_url: logoUrl });
}

export async function removeWorkspaceLogo(workspaceId: string, currentLogoUrl: string | null): Promise<{ workspace: Workspace }> {
  // Update workspace to remove logo reference
  const updated = await updateWorkspace(workspaceId, { logo_url: null });

  // Also delete the file from storage if it existed
  if (currentLogoUrl) {
    try {
      await fetchT(`${API_BASE_URL}/api/files/delete`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ url: currentLogoUrl }),
      });
    } catch {
      // Silently ignore file deletion failures - the reference is already removed
    }
  }

  return updated;
}
